/**
 * `window.matchMedia`, kept live — the primitive `Carousel.tsx` builds its
 * pause/breakpoint rules on. Split into its own file (rather than living
 * beside `Carousel`) purely so `react-refresh/only-export-components` stays
 * quiet: a file mixing component and non-component exports breaks fast
 * refresh's ability to hot-swap the component in isolation.
 */
import { useEffect, useState } from 'react'

/**
 * ⚠️ **Guarded on `matchMedia` itself, not only on `window`.** jsdom (this
 * project's test environment) has a `window` but does not implement
 * `matchMedia` at all — calling it throws `TypeError: window.matchMedia is
 * not a function` in any test that mounts a `Carousel` without stubbing it
 * first, which is every test that renders the homepage rather than the
 * carousel in isolation. Falling back to "not matching" is also the
 * conservative choice for the unknown case: it means `useCarouselPerView`
 * defaults to 1 (never claims more room than proven) and
 * `usePrefersReducedMotion` defaults to `false` (never silently disables
 * auto-play the caller asked for).
 */
function useMediaQuery(query: string): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function'

  const [matches, setMatches] = useState(() =>
    supported ? window.matchMedia(query).matches : false,
  )

  useEffect(() => {
    if (!supported) return
    const list = window.matchMedia(query)
    const onChange = () => setMatches(list.matches)
    onChange()
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query, supported])

  return matches
}

/**
 * `prefers-reduced-motion: reduce` — the first read of this media feature
 * anywhere in this codebase (roadmap 20.9). Exported because a carousel is
 * unlikely to stay the only moving thing on the homepage forever, and the
 * next one should read this rather than reinvent it.
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)')
}

/** 1 card under the `sm` breakpoint (420px cannot fit three cards), 3 at and above it. */
export function useCarouselPerView(): number {
  const wide = useMediaQuery('(min-width: 640px)')
  return wide ? 3 : 1
}

/**
 * Cards per page for the homepage's paged grid — two rows of whatever the
 * columns are (owner ruling, 1 October 2026: the reference's 3×2 page, and two
 * cards per page on a phone). It must agree with the grid's own column classes
 * (`sm:grid-cols-2 md:grid-cols-3`), or a page would end mid-row:
 *
 * - under 640px: one column → 2 per page
 * - 640–767px: two columns → 4 per page (three 200px cards are too narrow for
 *   a serif title and a date on one line)
 * - 768px and up: three columns → 6 per page
 */
export function usePagedGridPerPage(): number {
  const two = useMediaQuery('(min-width: 640px)')
  const three = useMediaQuery('(min-width: 768px)')
  return three ? 6 : two ? 4 : 2
}
